import { useState } from 'react'
import { FileUp, X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { inlineInput } from '@/lib/form-styles'
import { importLv, inspectLv, reviseLv, type LvZeichensatz } from '../api/calculation-lv.api'
import type { CalcLvPreview, CalcLvPreviewGroup, Calculation } from '../types/calculation.types'

const PHASE: Record<string, string> = { TENDER: 'Ausschreibung', OFFER: 'Angebot', ORDER: 'Auftrag', OTHER: '—' }
const ART: Record<string, string> = { NORMAL: 'Normal', OPTIONAL: 'Eventual', ALTERNATIVE: 'Wahl', REGIE: 'Regie', SURCHARGE: 'Zuschlag', TEXT: 'Text' }
const STATUS: Record<string, string> = { CHANGED: 'geändert', NEW: 'neu', REMOVED: 'entfallen' }
const FELD: Record<string, string> = { quantity: 'Menge', unit: 'Einheit', shortText: 'Kurztext', longText: 'Langtext', kind: 'Art', unitPrice: 'Preis' }
const wert = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'number' ? v.toLocaleString('de-DE') : String(v).slice(0, 40))

function Baum({ gruppen, tiefe = 0 }: { gruppen: CalcLvPreviewGroup[]; tiefe?: number }) {
  return (
    <>
      {gruppen.map((g) => (
        <div key={g.oz}>
          <div className="flex justify-between gap-2 text-sm" style={{ paddingLeft: `${tiefe * 1}rem` }}>
            <span className="truncate">
              <span className="tabular-nums text-muted-foreground">{g.oz}</span> {g.name}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">{g.items}</span>
          </div>
          <Baum gruppen={g.groups} tiefe={tiefe + 1} />
        </div>
      ))}
    </>
  )
}

/**
 * AP-37 4.11 — **LV einlesen**: Datei → Erkennen (Format, Fassung, Phase,
 * Zeichensatz, Zähler) → Vorschau (Kopf, Gliederung, Zähler je Art, Einheiten
 * mit Übersetzung, Bieterlücken, Lohn/Sonstiges gefordert) → **Einlesen**.
 * Mit `neueFassung` ist es eine geänderte Ausschreibung: Die Vorschau zeigt den
 * Abgleich je OZ, eingelesen wird als neue Fassung.
 */
export function LvEinlesenDialog({
  kalkulation,
  neueFassung = false,
  onClose,
  onDone,
}: {
  kalkulation: Calculation
  neueFassung?: boolean
  onClose: () => void
  onDone: (neueId?: string) => void
}) {
  const [datei, setDatei] = useState<File | null>(null)
  const [zeichensatz, setZeichensatz] = useState<LvZeichensatz | ''>('')
  const [vorschau, setVorschau] = useState<CalcLvPreview | null>(null)
  const [kopf, setKopf] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const pruefen = async (d: File, zs: LvZeichensatz | '') => {
    setLaeuft(true)
    setFehler(null)
    setVorschau(null)
    try {
      setVorschau(await inspectLv(kalkulation.id, d, zs || undefined))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Datei ließ sich nicht lesen.'))
    } finally {
      setLaeuft(false)
    }
  }

  const einlesen = async () => {
    if (!datei) return
    setLaeuft(true)
    setFehler(null)
    try {
      if (neueFassung) {
        const f = await reviseLv(kalkulation.id, datei, zeichensatz || undefined)
        onDone(f.id)
      } else {
        await importLv(kalkulation.id, datei, { encoding: zeichensatz || undefined, headFields: kopf })
        onDone()
      }
    } catch (e) {
      setFehler(apiErrorText(e, 'Das LV ließ sich nicht einlesen.'))
    } finally {
      setLaeuft(false)
    }
  }

  const v = vorschau
  const zaehler = v ? Object.entries(v.stats.byKind).filter(([, n]) => n > 0) : []
  const falscherZeichensatz = v?.notes.some((n) => n.code === 'LV_ENCODING')

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-primary">{neueFassung ? 'Geänderte Ausschreibung einlesen' : 'LV einlesen'}</h2>
            <p className="text-xs text-muted-foreground">
              ONLV (ÖNORM A 2063), ÖNORM B 2063 oder GAEB DA XML (X81, X83, X84, X86).{' '}
              {neueFassung ? 'Eingelesen wird als neue Fassung; die Kalkulation der Positionen geht je OZ mit.' : 'Legt Gruppen und gesperrte Positionen an — ausgepreist wird frei.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted">
            <FileUp className="h-4 w-4" /> {datei ? datei.name : 'Datei wählen …'}
            <input
              type="file"
              className="hidden"
              accept=".onlv,.xml,.zip,.x81,.x83,.x84,.x86,.dtn,.txt"
              onChange={(e) => {
                const d = e.target.files?.[0] ?? null
                setDatei(d)
                if (d) void pruefen(d, zeichensatz)
              }}
            />
          </label>
          {(v?.format === 'B2063' || falscherZeichensatz) && (
            <label className="flex items-center gap-1 text-sm">
              Zeichensatz
              <select
                className={`${inlineInput} !w-36`}
                value={zeichensatz}
                onChange={(e) => {
                  const zs = e.target.value as LvZeichensatz | ''
                  setZeichensatz(zs)
                  if (datei) void pruefen(datei, zs)
                }}
              >
                <option value="">erkannt ({v?.encoding})</option>
                <option value="cp850">CP850 (DOS)</option>
                <option value="latin1">ISO 8859-1</option>
                <option value="utf-8">UTF-8</option>
              </select>
            </label>
          )}
          {laeuft && <span className="text-sm text-muted-foreground">Lese …</span>}
        </div>

        {v && (
          <div className="mt-4 space-y-4">
            <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <div>
                <span className="text-muted-foreground">Format:</span> {v.format} {v.formatVersion} · {PHASE[v.phase] ?? v.phase}
              </div>
              <div>
                <span className="text-muted-foreground">Bezeichnung:</span> {v.title}
              </div>
              {v.project && (
                <div>
                  <span className="text-muted-foreground">Vorhaben:</span> {v.project}
                </div>
              )}
              {v.client && (
                <div>
                  <span className="text-muted-foreground">Auftraggeber:</span> {v.client}
                </div>
              )}
              {v.dueAt && (
                <div>
                  <span className="text-muted-foreground">Angebotsfrist:</span> {new Date(v.dueAt).toLocaleDateString('de-DE')}
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Positionen:</span> {v.stats.items} in {v.stats.groups} Gruppen
                {zaehler.length > 1 && ` (${zaehler.map(([k, n]) => `${n} ${ART[k] ?? k}`).join(', ')})`}
              </div>
              {v.stats.gaps > 0 && (
                <div>
                  <span className="text-muted-foreground">Bieterlücken:</span> {v.stats.gaps}
                </div>
              )}
              {v.requiresPriceSplit && <div className="font-medium">Lohn und Sonstiges sind gefordert</div>}
            </div>

            {v.comparison && (
              <div className="rounded-md border border-border p-3">
                <div className="mb-2 text-sm font-semibold">
                  Abgleich mit dem bisherigen LV: {v.comparison.changed} geändert · {v.comparison.new} neu · {v.comparison.removed} entfallen · {v.comparison.unchanged} unverändert
                </div>
                <div className="max-h-48 overflow-auto text-sm">
                  {v.comparison.lines.map((l) => (
                    <div key={`${l.status}-${l.oz}`} className="flex gap-2 py-0.5">
                      <span className="w-20 shrink-0 tabular-nums text-muted-foreground">{l.oz}</span>
                      <span className={`w-20 shrink-0 text-xs ${l.status === 'REMOVED' ? 'text-destructive' : l.status === 'NEW' ? 'text-emerald-700' : 'text-amber-700'}`}>{STATUS[l.status]}</span>
                      <span className="min-w-0 flex-1 truncate">
                        {l.shortText}
                        {l.changes.length > 0 && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {l.changes.map((c) => `${FELD[c.field] ?? c.field}: ${wert(c.before)} → ${wert(c.after)}`).join(' · ')}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Gliederung</div>
                <div className="max-h-48 overflow-auto rounded-md border border-border p-2">
                  <Baum gruppen={v.groups} />
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Einheiten</div>
                <div className="max-h-48 overflow-auto rounded-md border border-border p-2 text-sm">
                  {v.units.map((u) => (
                    <div key={u.unit} className="flex justify-between gap-2">
                      <span>
                        {u.unit} <span className="text-xs text-muted-foreground">({u.count})</span>
                      </span>
                      {u.unitCode ? <span className="text-muted-foreground">→ {u.unitCode}</span> : <span className="text-destructive">ohne Übersetzung</span>}
                    </div>
                  ))}
                </div>
                {v.units.some((u) => !u.unitCode) && <p className="mt-1 text-xs text-muted-foreground">Ohne Übersetzung bleibt die Einheit laut LV stehen; übersetzt wird danach im Reiter LV.</p>}
              </div>
            </div>

            {v.notes.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                {v.notes.slice(0, 8).map((n, i) => (
                  <li key={i}>{n.text}</li>
                ))}
              </ul>
            )}

            {!neueFassung && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={kopf} onChange={(e) => setKopf(e.target.checked)} />
                Bauvorhaben, Auftraggeber (als Interessent) und Abgabetermin übernehmen, wo leer
              </label>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={!v || laeuft} onClick={() => void einlesen()}>
            {neueFassung ? 'Als neue Fassung einlesen' : 'Einlesen'}
          </button>
        </div>
      </div>
    </div>
  )
}
